// Roster spatial-first tours (U8): F4 tour-assignment migration dialog.
//
// Operator flow: pick an existing tour (or "+ Create new tour" inline) →
// confirm tier per distinct service_location_label on the event → submit
// fires the atomic PATCH /api/windows/{id} with intent="assign_tour".
//
// Backend orchestrates the multi-step promotion in a single transaction
// (vocab append + per-membership tag + window.tour_id set). The dialog is
// staged client-side per the plan's Outstanding Questions decision —
// no SELECT FOR UPDATE during operator deliberation.
//
// Error surfaces (each rendered inline so the operator can correct without
// losing state):
//   - 400 nothing_to_migrate    → "Tag at least one service first."
//   - 400 tier_map_incomplete   → highlight rows that need a tier picked
//   - 400 invalid_tier          → defensive; UI radios make this unreachable
//   - 409 tier_conflict         → per-label conflict UI listing existing tier
//   - 400 tour_not_found / tour_deleted → toast + close (someone deleted)
//
// One-off-event path is OUT of scope for this dialog — F3 (tour_id NULL)
// keeps using service_location_label directly with no migration.

import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  api,
  assignTourToWindow,
  createTour,
  getTours,
} from "@/lib/api";
import type { Service, Tour, Window } from "@/lib/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  window: Window;
  open: boolean;
  onClose: () => void;
  onAssigned: () => void;
}

type Tier = "stage" | "house";

// Per-label conflict shape returned by the backend on 409 tier_conflict.
interface TierConflict {
  label: string;
  existing_tier: Tier;
  requested_tier: Tier;
}

export function TourAssignmentDialog({
  window: windowRow,
  open,
  onClose,
  onAssigned,
}: Props) {
  // Step state — null tour means we're on the picker; tour set means we're
  // on the tier-confirmation step. Reset on close.
  const [tourList, setTourList] = useState<Tour[]>([]);
  const [tourListLoaded, setTourListLoaded] = useState(false);
  const [selectedTour, setSelectedTour] = useState<Tour | null>(null);

  // Distinct service_location_label values among the event's open memberships.
  // The tier-picker renders one row per entry; operator confirms a tier per
  // row before submit.
  const [distinctLabels, setDistinctLabels] = useState<string[]>([]);
  const [labelsLoaded, setLabelsLoaded] = useState(false);

  const [tierMap, setTierMap] = useState<Record<string, Tier>>({});
  const [missingLabels, setMissingLabels] = useState<Set<string>>(new Set());
  const [conflicts, setConflicts] = useState<TierConflict[]>([]);

  // Inline create-tour affordance (used when the picker is empty or operator
  // wants a fresh tour mid-flow). Mirrors the U6-deferral note: tour CRUD UI
  // is deferred; this inline form is the minimum viable entry point.
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createArtist, setCreateArtist] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch tours + distinct labels when the dialog opens. We fetch both
  // unconditionally — label-distinct is cheap and the second view needs it
  // ready by the time the operator picks a tour.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTourListLoaded(false);
    setLabelsLoaded(false);
    getTours()
      .then((r) => { if (!cancelled) { setTourList(r.tours); setTourListLoaded(true); } })
      .catch(() => { if (!cancelled) { setTourList([]); setTourListLoaded(true); } });
    api
      .get<{ services: Service[] }>(
        `/api/services?window_id=${encodeURIComponent(windowRow.window_id)}`,
      )
      .then((r) => {
        if (cancelled) return;
        // Distinct + non-null + non-empty. Sorted for stable rendering.
        const seen = new Set<string>();
        for (const s of r.services) {
          const v = (s.service_location_label ?? "").trim();
          if (v) seen.add(v);
        }
        setDistinctLabels(Array.from(seen).sort());
        setLabelsLoaded(true);
      })
      .catch(() => { if (!cancelled) { setDistinctLabels([]); setLabelsLoaded(true); } });
    return () => { cancelled = true; };
  }, [open, windowRow.window_id]);

  // When the operator picks a tour, pre-fill tier_map entries for any
  // distinct label that's already in the tour's vocab — saves a click and
  // makes the agreement-with-existing-vocab path the default. Operator can
  // still flip the radio if they want to force a 409 surface.
  useEffect(() => {
    if (!selectedTour) return;
    const vocab = selectedTour.touring_service_vocabulary ?? [];
    const byLower: Record<string, Tier> = {};
    for (const e of vocab) byLower[e.label.toLowerCase()] = e.tier;
    setTierMap((prev) => {
      const next = { ...prev };
      for (const label of distinctLabels) {
        if (next[label] === undefined) {
          const t = byLower[label.toLowerCase()];
          if (t) next[label] = t;
        }
      }
      return next;
    });
  }, [selectedTour, distinctLabels]);

  // Reset every interaction-derived field on close so re-opening the dialog
  // never bleeds prior state.
  const resetAll = () => {
    setSelectedTour(null);
    setTierMap({});
    setMissingLabels(new Set());
    setConflicts([]);
    setShowCreate(false);
    setCreateName("");
    setCreateArtist("");
    setSubmitting(false);
    setError(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && !submitting) {
      resetAll();
      onClose();
    }
  };

  const onCreateTour = async () => {
    const name = createName.trim();
    if (!name) {
      setError("Tour name is required.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const fresh = await createTour({
        name,
        artist: createArtist.trim() || undefined,
      });
      // Refresh the list so picker is consistent, then jump straight to
      // tier-confirmation with the new tour selected.
      const r = await getTours();
      setTourList(r.tours);
      setSelectedTour(fresh);
      setShowCreate(false);
      setCreateName("");
      setCreateArtist("");
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async () => {
    if (!selectedTour) return;
    // Local validation mirrors backend tier_map_incomplete to give an
    // immediate hint without burning a round-trip.
    const localMissing = new Set<string>(
      distinctLabels.filter((l) => tierMap[l] === undefined),
    );
    if (localMissing.size > 0) {
      setMissingLabels(localMissing);
      setError("Pick a tier for every service.");
      return;
    }
    setMissingLabels(new Set());
    setConflicts([]);
    setError(null);
    setSubmitting(true);
    try {
      await assignTourToWindow(
        windowRow.window_id,
        selectedTour.tour_id,
        tierMap,
      );
      onAssigned();
      resetAll();
      onClose();
    } catch (e) {
      // Per-error surfacing — `tier_conflict` keeps the dialog open with the
      // conflicts laid out next to the rows; other 4xx errors render as a
      // top-level message.
      if (e instanceof ApiError) {
        const body = e.body as
          | {
              error?: string;
              missing?: string[];
              conflicts?: TierConflict[];
              message?: string;
            }
          | undefined;
        if (e.status === 409 && body?.error === "tier_conflict" && body.conflicts) {
          setConflicts(body.conflicts);
          setError(
            body.message
              ?? "One or more labels conflict with the tour's existing tier.",
          );
        } else if (e.status === 400 && body?.error === "tier_map_incomplete") {
          setMissingLabels(new Set(body.missing ?? []));
          setError("Tier missing for one or more services.");
        } else if (e.status === 400 && body?.error === "nothing_to_migrate") {
          setError(
            body.message
              ?? "No service has a location tagged. Tag at least one service first.",
          );
        } else if (e.status === 400 && body?.error === "tour_not_found") {
          setError("That tour no longer exists. Pick another or create one.");
          setSelectedTour(null);
        } else if (e.status === 400 && body?.error === "tour_deleted") {
          setError("That tour was deleted. Pick another or create one.");
          setSelectedTour(null);
        } else {
          setError(friendlyError(e));
        }
      } else {
        setError(friendlyError(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Picker view — also surfaces the inline create-tour affordance + an empty
  // state nudge if the deployment has no tours yet.
  const renderPicker = () => (
    <>
      <DialogDescription>
        Pick the tour this event belongs to. Existing service-location tags
        will be promoted into the tour's vocabulary in the next step.
      </DialogDescription>

      {!tourListLoaded ? (
        <div className="text-sm text-muted-foreground py-2">Loading tours…</div>
      ) : tourList.length === 0 && !showCreate ? (
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">
            No tours yet for this deployment.
          </div>
          <Button onClick={() => setShowCreate(true)} variant="outline">
            + Create new tour
          </Button>
        </div>
      ) : showCreate ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="new-tour-name">Tour name</Label>
            <Input
              id="new-tour-name"
              value={createName}
              autoFocus
              maxLength={200}
              placeholder="e.g. The Eternal Sunshine Tour"
              onChange={(e) => setCreateName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-tour-artist">Artist (optional)</Label>
            <Input
              id="new-tour-artist"
              value={createArtist}
              maxLength={200}
              placeholder="e.g. AG"
              onChange={(e) => setCreateArtist(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={onCreateTour} disabled={submitting}>
              {submitting ? "Creating…" : "Create + continue"}
            </Button>
            <Button
              variant="outline"
              onClick={() => { setShowCreate(false); setError(null); }}
              disabled={submitting}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="tour-picker">Tour</Label>
          <select
            id="tour-picker"
            className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            value=""
            onChange={(e) => {
              const t = tourList.find((x) => x.tour_id === e.target.value);
              if (t) setSelectedTour(t);
            }}
          >
            <option value="" disabled>
              — select tour —
            </option>
            {tourList.map((t) => (
              <option key={t.tour_id} value={t.tour_id}>
                {t.name}
                {t.artist ? ` · ${t.artist}` : ""}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCreate(true)}
          >
            + Create new tour
          </Button>
        </div>
      )}
    </>
  );

  // Map conflicts to per-label lookups for inline-row decoration.
  const conflictByLabelLower = useMemo(() => {
    const m: Record<string, TierConflict> = {};
    for (const c of conflicts) m[c.label.toLowerCase()] = c;
    return m;
  }, [conflicts]);

  // Tier-confirmation view — one row per distinct label.
  const renderTierConfirm = () => (
    <>
      <DialogDescription>
        Confirm a tier for each service-location label. New labels will be
        appended to <span className="font-medium">{selectedTour?.name}</span>'s
        vocabulary; labels that already exist keep their existing tier (you
        can override but the server will refuse mismatched tiers).
      </DialogDescription>

      {!labelsLoaded ? (
        <div className="text-sm text-muted-foreground py-2">Loading services…</div>
      ) : distinctLabels.length === 0 ? (
        <div className="text-sm text-muted-foreground py-2">
          No services on this event have a location label set yet. Tag at
          least one from the load-in screen, then re-open this dialog.
        </div>
      ) : (
        <div className="space-y-3">
          {distinctLabels.map((label) => {
            const tier = tierMap[label];
            const conflict = conflictByLabelLower[label.toLowerCase()];
            const isMissing = missingLabels.has(label);
            return (
              <div
                key={label}
                className={
                  "flex items-center justify-between gap-3 rounded-md border px-3 py-2 "
                  + (conflict
                    ? "border-destructive/40 bg-destructive/5"
                    : isMissing
                      ? "border-amber-500/40 bg-amber-500/5"
                      : "border-border")
                }
              >
                <div className="text-sm">
                  <div className="font-medium">{label}</div>
                  {conflict && (
                    <div className="text-xs text-destructive mt-0.5">
                      Tour vocab has tier{" "}
                      <span className="font-mono">{conflict.existing_tier}</span>{" "}
                      — you picked{" "}
                      <span className="font-mono">{conflict.requested_tier}</span>.
                    </div>
                  )}
                </div>
                <div className="flex gap-3 text-sm">
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name={`tier-${label}`}
                      value="stage"
                      checked={tier === "stage"}
                      disabled={submitting}
                      onChange={() =>
                        setTierMap((m) => ({ ...m, [label]: "stage" }))
                      }
                    />
                    Stage
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name={`tier-${label}`}
                      value="house"
                      checked={tier === "house"}
                      disabled={submitting}
                      onChange={() =>
                        setTierMap((m) => ({ ...m, [label]: "house" }))
                      }
                    />
                    House
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {selectedTour
              ? `Assign ${windowRow.name} to ${selectedTour.name}`
              : `Assign ${windowRow.name} to a tour`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {selectedTour ? renderTierConfirm() : renderPicker()}
          {error && <div className="text-destructive text-sm">{error}</div>}
        </div>

        <DialogFooter>
          {selectedTour && (
            <Button
              variant="ghost"
              onClick={() => {
                setSelectedTour(null);
                setError(null);
                setConflicts([]);
                setMissingLabels(new Set());
              }}
              disabled={submitting}
            >
              ← Back
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          {selectedTour && (
            <Button
              onClick={onSubmit}
              disabled={
                submitting
                || !labelsLoaded
                || distinctLabels.length === 0
              }
            >
              {submitting ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-r-transparent" />
                  Saving…
                </span>
              ) : (
                "Assign tour & tag services"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string; message?: string } | undefined;
    return body?.message ?? body?.error ?? `Server error ${e.status}`;
  }
  return String(e);
}
